import { useDispatch, useSelector } from "react-redux";
import {
  setSelectedTicketsSortHeader,
  setTicketFilterType,
  setTicketsPage,
  setTicketsSortHeader,
  setTicketStatusType,
  setFlightStatusFilterValues,
  setTicketStatusFilterValues,
  setTicketType,
} from "../redux/reducers/orderSlice";
import { templateFlightOrderData } from "../data/order/ordersDataV2";
import useOrdersSort from "./useOrdersSort";
import sortArrayWithIndices from "../features/utils/sortWithIndice";
import { templateTicketsOrderData } from "../data/order/ordersDataTickets";
import { templateFlightOrderVoidData } from "../data/order/ordersVoidData";
import { templateFlightOrderRefundData } from "../data/order/ordersRefundData";
import { templateFlightOrderReissueData } from "../data/order/ordersReissueData";
import { templateStayOrderData } from "../data/order/stays/stayOrdersData";
import { templateTourOrderData } from "../data/order/tours/tourOrdersData";
import { configurations } from "../configurations";
import { templateInsuranceOrderData } from "../data/order/insurance/insuranceOrdersData";
import { templateFlightOrderRenameData } from "../data/order/ordersRenameData";
import { templateFlightOrderOpenTicketData } from "../data/order/ordersOpenTicketData";
import { templatePackageOrderData } from "../data/order/package/packageOrdersData";
import { templateEsimOrderData } from "../data/order/esim/esimOrdersData";

export default function useOrders() {
  const { sortFlightOrders } = useOrdersSort();
  const dispatch = useDispatch();
  const {
    ticketsSortHeader,
    selectedTicketsSortHeader,
    ticketType,
    flightOrders,
    voidOrders,
    refundOrders,
    reissueOrders,
    renameOrders,
    openTicketOrders,
    hotelOrders,
    tourOrders,
    insuranceOrders,
    ticketFilterType,
    ticketSearchValue,
    ticketStatusType,
    packageOrders,
    packageTotal,
    esimOrders,
    esimTotal,
  } = useSelector((state) => state.order);

  const getOrdersCount = () => {
    return {
      flights: flightOrders?.length || 0,
      stays: hotelOrders?.length || 0,
      tour: tourOrders?.length || 0,
      insurance: insuranceOrders?.length || 0,
      package: packageTotal || packageOrders?.length || 0,
      esim: esimTotal || esimOrders?.length || 0,
    };
  };

  const selectOrderType = (orderType) => {
    dispatch(setTicketType(orderType));
    dispatch(setTicketsPage(1));
    if (orderType === "Stays") {
      dispatch(setTicketFilterType(hotelFilterType[0]?.name));
    } else if (orderType === "Tour" || orderType === "Tours") {
      dispatch(setTicketFilterType(tourFilterType[0]?.name));
    } else if (orderType === configurations.insuranceKeyword) {
      dispatch(setTicketFilterType(insuranceFilterType[0]?.name));
    } else if (orderType === configurations.services.package.value) {
      dispatch(setTicketFilterType("All"));
    } else if (orderType === configurations.services.esim.value) {
      dispatch(setTicketFilterType("All"));
    }
  };

  const ticketFilters = {
    flight: [
      {
        name: "All",
        onClick: () => {
          dispatch(setTicketsPage(1));
          dispatch(setTicketStatusType("All"));
          dispatch(setFlightStatusFilterValues([]));
        },
      },
      {
        name: "Pending Payment",
        onClick: () => {
          dispatch(setTicketsPage(1));
          dispatch(setTicketStatusType("Pending Payment"));
          dispatch(setFlightStatusFilterValues(["Booked", "Pending"]));
        },
      },
      {
        name: "Paid",
        onClick: () => {
          dispatch(setTicketsPage(1));
          dispatch(setTicketStatusType("Paid"));
          dispatch(setFlightStatusFilterValues(["Paid"]));
        },
      },
      {
        name: "Expired",
        onClick: () => {
          dispatch(setTicketsPage(1));
          dispatch(setTicketStatusType("Expired"));
          dispatch(
            setFlightStatusFilterValues(["Expired", "AutoCanceled", "Failed"]),
          );
        },
      },
      {
        name: "Cancelled",
        onClick: () => {
          dispatch(setTicketsPage(1));
          dispatch(setTicketStatusType("Cancelled"));
          dispatch(setFlightStatusFilterValues(["Canceled", "Cancelled"]));
        },
      },
      {
        name: "Ticketing",
        onClick: () => {
          dispatch(setTicketsPage(1));
          dispatch(setTicketStatusType("Ticketing"));
          dispatch(
            setFlightStatusFilterValues([
              "PendingTicketIssue",
              "PendingTicketIssueApproval",
            ]),
          );
        },
      },
      {
        name: "Re-issue",
        onClick: () => {
          dispatch(setTicketsPage(1));
          dispatch(setTicketStatusType("Re-issue"));
          dispatch(setFlightStatusFilterValues(["ChangedOrder"]));
        },
      },
      {
        name: "Refund",
        onClick: () => {
          dispatch(setTicketsPage(1));
          dispatch(setTicketStatusType("Refund"));
          dispatch(setFlightStatusFilterValues(["Refunded"]));
        },
      },
      {
        name: "Void",
        onClick: () => {
          dispatch(setTicketsPage(1));
          dispatch(setTicketStatusType("Void"));
          dispatch(
            setFlightStatusFilterValues([
              "TicketCanceled",
              "TicketCancelationRequested",
            ]),
          );
        },
      },
      {
        name: "Completed",
        onClick: () => {
          dispatch(setTicketsPage(1));
          dispatch(setTicketStatusType("Completed"));
          dispatch(setFlightStatusFilterValues(["Issued"]));
        },
      },
    ],
    freeze: [
      {
        name: "All",
        onClick: () => {
          dispatch(setTicketsPage(1));
          dispatch(setTicketStatusType("All"));
          dispatch(
            setFlightStatusFilterValues([
              "Initiated",
              "Approved",
              "Declined",
              "Cancelled",
              "Completed",
            ]),
          );
        },
      },
      {
        name: "Initiated",
        onClick: () => {
          dispatch(setTicketsPage(1));
          dispatch(setTicketStatusType("Initiated"));
          dispatch(setFlightStatusFilterValues(["Initiated"]));
        },
      },
      {
        name: "Approved",
        onClick: () => {
          dispatch(setTicketsPage(1));
          dispatch(setTicketStatusType("Approved"));
          dispatch(setFlightStatusFilterValues(["Approved"]));
        },
      },
      {
        name: "Declined",
        onClick: () => {
          dispatch(setTicketsPage(1));
          dispatch(setTicketStatusType("Declined"));
          dispatch(setFlightStatusFilterValues(["Declined"]));
        },
      },
      {
        name: "Cancelled",
        onClick: () => {
          dispatch(setTicketsPage(1));
          dispatch(setTicketStatusType("Cancelled"));
          dispatch(setFlightStatusFilterValues(["Cancelled"]));
        },
      },
      {
        name: "Completed",
        onClick: () => {
          dispatch(setTicketsPage(1));
          dispatch(setTicketStatusType("Completed"));
          dispatch(setFlightStatusFilterValues(["Completed"]));
        },
      },
    ],
    ticket: [
      {
        name: "All",
        onClick: () => {
          dispatch(setTicketsPage(1));
          dispatch(setTicketStatusType("All"));
          dispatch(setTicketStatusFilterValues([]));
        },
      },
      {
        name: "Pending Ticket",
        onClick: () => {
          dispatch(setTicketsPage(1));
          dispatch(setTicketStatusType("Pending Ticket"));
          dispatch(setTicketStatusFilterValues(["Open"]));
        },
      },
      {
        name: "Completed",
        onClick: () => {
          dispatch(setTicketsPage(1));
          dispatch(setTicketStatusType("Completed"));
          dispatch(setTicketStatusFilterValues(["Issued"]));
        },
      },
      {
        name: "Rejected",
        onClick: () => {
          dispatch(setTicketsPage(1));
          dispatch(setTicketStatusType("Rejected"));
          dispatch(setTicketStatusFilterValues(["Denied"]));
        },
      },
    ],
    voidTicket: [
      {
        name: "All",
        onClick: () => {
          dispatch(setTicketsPage(1));
          dispatch(setTicketStatusType("All"));
          dispatch(setTicketStatusFilterValues([]));
        },
      },
      {
        name: "Pending",
        onClick: () => {
          dispatch(setTicketsPage(1));
          dispatch(setTicketStatusType("Pending"));
          dispatch(
            setTicketStatusFilterValues([
              "Pending",
              "PendingVoid",
              "Approved",
              "ManualReviewRequired",
              "MANUAL_REVIEW_REQUIRED",
              "AUTO_VOID_FAILED",
              "AutoVoidFailed",
              "VOID_REQUEST_SUBMITTED",
              "VoidRequestSubmitted",
              "MANUAL_VOID_PROCESSING",
              "ManualVoidProcessing",
              "VOID_PROCESSING",
              "VoidProcessing",
              "AUTO_VOID_PROCESSING",
              "AutoVoidProcessing",
              "SUBMITTED_TO_SUPPLIER",
              "SubmittedToSupplier",
              "SUPPLIER_PROCESSING",
              "SupplierProcessing",
              "VOID_SUCCESSFUL",
              "VoidSuccessful",
              "SETTLEMENT_PENDING",
              "SettlementPending",
              "SERVICE_FEE_DEBITED",
              "ServiceFeeDebited",
              "BALANCE_REFUNDED",
              "BalanceRefunded",
              "SETTLEMENT_FAILED",
              "SettlementFailed",
              "VOID_FAILED_BY_SUPPLIER",
              "VoidFailedBySupplier",
              "VOID_DECLINED_BY_SUPPLIER",
              "VoidDeclinedBySupplier",
              "VOID_REQUEST_VALIDATION_FAILED",
              "VoidRequestValidationFailed",
              "Failed",
            ]),
          );
        },
      },
      {
        name: "Rejected",
        onClick: () => {
          dispatch(setTicketsPage(1));
          dispatch(setTicketStatusType("Rejected"));
          dispatch(setTicketStatusFilterValues(["Denied", "Rejected", "Cancelled", "Canceled"]));
        },
      },
      {
        name: "Completed",
        onClick: () => {
          dispatch(setTicketsPage(1));
          dispatch(setTicketStatusType("Completed"));
          dispatch(
            setTicketStatusFilterValues([
              "Success",
              "Closed",
              "VOID_COMPLETED",
              "VoidCompleted",
              "VOID_FAILURE_SETTLED",
              "VoidFailureSettled",
            ]),
          );
        },
      },
    ],
    refundTicket: [
      {
        name: "All",
        onClick: () => {
          dispatch(setTicketsPage(1));
          dispatch(setTicketStatusType("All"));
          dispatch(setTicketStatusFilterValues([]));
        },
      },
      {
        name: "Pending",
        onClick: () => {
          dispatch(setTicketsPage(1));
          dispatch(setTicketStatusType("Pending"));
          dispatch(
            setTicketStatusFilterValues([
              "Pending",
              "PendingVoid",
              "PendingRefund",
              "Approved",
              "ManualReviewRequired",
              "MANUAL_REVIEW_REQUIRED",
              "AUTO_REFUND_FAILED",
              "AutoRefundFailed",
              "REFUND_REQUEST_SUBMITTED",
              "RefundRequestSubmitted",
              "AUTO_REFUND_PROCESSING",
              "AutoRefundProcessing",
              "ManualRefundProcessing",
              "MANUAL_REFUND_PROCESSING",
              "RefundQuoted",
              "REFUND_QUOTED",
              "REFUND_QUOTE_EXPIRED",
              "RefundQuoteExpired",
              "RefundAccepted",
              "REFUND_ACCEPTED",
              "REFUND_SUBMITTING",
              "RefundSubmitting",
              "AUTO_REFUND_SUBMISSION",
              "AutoRefundSubmission",
              "AUTO_REFUND_SUBMISSION_FAILED",
              "AutoRefundSubmissionFailed",
              "SUBMITTED_TO_SUPPLIER",
              "SubmittedToSupplier",
              "SupplierProcessing",
              "SUPPLIER_PROCESSING",
              "REFUND_SUCCESSFUL",
              "RefundSuccessful",
              "SETTLEMENT_PENDING",
              "SettlementPending",
              "SETTLEMENT_FAILED",
              "SettlementFailed",
              "REFUND_FAILED_BY_SUPPLIER",
              "RefundFailedBySupplier",
              "REFUND_DECLINED_BY_SUPPLIER",
              "RefundDeclinedBySupplier",
              "REFUND_VALIDATION_FAILED",
              "RefundValidationFailed",
              "Failed",
            ]),
          );
        },
      },
      {
        name: "Rejected",
        onClick: () => {
          dispatch(setTicketsPage(1));
          dispatch(setTicketStatusType("Rejected"));
          dispatch(
            setTicketStatusFilterValues([
              "Denied",
              "Rejected",
              "Cancelled",
              "Canceled",
              "REFUND_DECLINED_BY_AGENT",
              "RefundDeclinedByAgent",
            ]),
          );
        },
      },
      {
        name: "Completed",
        onClick: () => {
          dispatch(setTicketsPage(1));
          dispatch(setTicketStatusType("Completed"));
          dispatch(
            setTicketStatusFilterValues([
              "Success",
              "Closed",
              "RefundCompleted",
              "REFUND_COMPLETED",
            ]),
          );
        },
      },
    ],
    dateChangeTicket: [
      {
        name: "All",
        onClick: () => {
          dispatch(setTicketsPage(1));
          dispatch(setTicketStatusType("All"));
          dispatch(setTicketStatusFilterValues([]));
        },
      },
      {
        name: "New",
        onClick: () => {
          dispatch(setTicketsPage(1));
          dispatch(setTicketStatusType("New"));
          dispatch(
            setTicketStatusFilterValues([
              "Pending",
              "DATE_CHANGE_REQUEST_SUBMITTED",
              "DateChangeRequestSubmitted",
            ]),
          );
        },
      },
      {
        name: "Needs review",
        onClick: () => {
          dispatch(setTicketsPage(1));
          dispatch(setTicketStatusType("Needs review"));
          dispatch(
            setTicketStatusFilterValues([
              "ManualReviewRequired",
              "MANUAL_REVIEW_REQUIRED",
              "DATE_CHANGE_VALIDATION_FAILED",
              "DateChangeValidationFailed",
            ]),
          );
        },
      },
      {
        name: "Processing",
        onClick: () => {
          dispatch(setTicketsPage(1));
          dispatch(setTicketStatusType("Processing"));
          dispatch(
            setTicketStatusFilterValues([
              "Approved",
              "AUTO_DATE_CHANGE_PROCESSING",
              "AutoDateChangeProcessing",
              "MANUAL_DATE_CHANGE_PROCESSING",
              "ManualDateChangeProcessing",
              "DATE_CHANGE_QUOTED",
              "DateChangeQuoted",
              "DATE_CHANGE_ACCEPTED",
              "DateChangeAccepted",
              "DATE_CHANGE_QUOTE_EXPIRED",
              "DateChangeQuoteExpired",
              "PAYMENT_REQUIRED",
              "PaymentRequired",
              "PAYMENT_AUTHORISED",
              "PAYMENT_AUTHORIZED",
              "PaymentAuthorised",
              "PAYMENT_NOT_REQUIRED",
              "PaymentNotRequired",
              "REFUND_DUE",
              "RefundDue",
              "DATE_CHANGE_SUBMITTING",
              "DateChangeSubmitting",
              "AUTO_CHANGE_SUBMISSION",
              "AutoChangeSubmission",
              "AUTO_CHANGE_SUBMISSION_FAILED",
              "AutoChangeSubmissionFailed",
              "SUBMITTED_TO_SUPPLIER",
              "SubmittedToSupplier",
              "SUPPLIER_PROCESSING",
              "SupplierProcessing",
              "DATE_CHANGE_SUCCESSFUL",
              "DateChangeSuccessful",
              "SETTLEMENT_PENDING",
              "SettlementPending",
              "ADDITIONAL_COLLECTION_DEBITED",
              "AdditionalCollectionDebited",
              "NO_FINANCIAL_ADJUSTMENT",
              "NoFinancialAdjustment",
              "BALANCE_REFUNDED",
              "BalanceRefunded",
              "SETTLEMENT_FAILED",
              "SettlementFailed",
              "SETTLEMENT_REVERSAL_PENDING",
              "SettlementReversalPending",
              "PAYMENT_RESERVATION_RELEASED",
              "PaymentReservationReleased",
              "PAYMENT_REVERSED",
              "PaymentReversed",
              "Paid",
              "PendingChange",
              "PendingReissue",
              "Pending Reissue",
              "Failed",
            ]),
          );
        },
      },
      {
        name: "Auto failed",
        onClick: () => {
          dispatch(setTicketsPage(1));
          dispatch(setTicketStatusType("Auto failed"));
          dispatch(
            setTicketStatusFilterValues([
              "AUTO_DATE_CHANGE_FAILED",
              "AutoDateChangeFailed",
            ]),
          );
        },
      },
      {
        name: "Rejected",
        onClick: () => {
          dispatch(setTicketsPage(1));
          dispatch(setTicketStatusType("Rejected"));
          dispatch(
            setTicketStatusFilterValues([
              "Denied",
              "Rejected",
              "Cancelled",
              "Canceled",
              "DATE_CHANGE_DECLINED_BY_AGENT",
              "DateChangeDeclinedByAgent",
              "DATE_CHANGE_DECLINED_BY_SUPPLIER",
              "DateChangeDeclinedBySupplier",
              "DATE_CHANGE_FAILED_BY_SUPPLIER",
              "DateChangeFailedBySupplier",
              "DATE_CHANGE_FAILURE_SETTLED",
              "DateChangeFailureSettled",
            ]),
          );
        },
      },
      {
        name: "Completed",
        onClick: () => {
          dispatch(setTicketsPage(1));
          dispatch(setTicketStatusType("Completed"));
          dispatch(
            setTicketStatusFilterValues([
              "Success",
              "Closed",
              "ChangedOrder",
              "DATE_CHANGE_COMPLETED",
              "DateChangeCompleted",
            ]),
          );
        },
      },
    ],
    changeTicket: [
      {
        name: "All",
        onClick: () => {
          dispatch(setTicketsPage(1));
          dispatch(setTicketStatusType("All"));
          dispatch(setTicketStatusFilterValues([]));
        },
      },
      {
        name: "Pending",
        onClick: () => {
          dispatch(setTicketsPage(1));
          dispatch(setTicketStatusType("Pending"));
          dispatch(
            setTicketStatusFilterValues([
              "Pending",
              "PendingVoid",
              "Approved",
              "ManualReviewRequired",
              "MANUAL_REVIEW_REQUIRED",
              "DATE_CHANGE_REQUEST_SUBMITTED",
              "DateChangeRequestSubmitted",
              "DATE_CHANGE_VALIDATION_FAILED",
              "DateChangeValidationFailed",
              "AUTO_DATE_CHANGE_PROCESSING",
              "AutoDateChangeProcessing",
              "AUTO_DATE_CHANGE_FAILED",
              "AutoDateChangeFailed",
              "MANUAL_DATE_CHANGE_PROCESSING",
              "ManualDateChangeProcessing",
              "DATE_CHANGE_QUOTED",
              "DateChangeQuoted",
              "DATE_CHANGE_ACCEPTED",
              "DateChangeAccepted",
              "DATE_CHANGE_QUOTE_EXPIRED",
              "DateChangeQuoteExpired",
              "PAYMENT_REQUIRED",
              "PaymentRequired",
              "PAYMENT_AUTHORISED",
              "PAYMENT_AUTHORIZED",
              "PaymentAuthorised",
              "PAYMENT_NOT_REQUIRED",
              "PaymentNotRequired",
              "REFUND_DUE",
              "RefundDue",
              "DATE_CHANGE_SUBMITTING",
              "DateChangeSubmitting",
              "AUTO_CHANGE_SUBMISSION",
              "AutoChangeSubmission",
              "AUTO_CHANGE_SUBMISSION_FAILED",
              "AutoChangeSubmissionFailed",
              "SUBMITTED_TO_SUPPLIER",
              "SubmittedToSupplier",
              "SUPPLIER_PROCESSING",
              "SupplierProcessing",
              "DATE_CHANGE_SUCCESSFUL",
              "DateChangeSuccessful",
              "SETTLEMENT_PENDING",
              "SettlementPending",
              "ADDITIONAL_COLLECTION_DEBITED",
              "AdditionalCollectionDebited",
              "NO_FINANCIAL_ADJUSTMENT",
              "NoFinancialAdjustment",
              "BALANCE_REFUNDED",
              "BalanceRefunded",
              "SETTLEMENT_FAILED",
              "SettlementFailed",
              "SETTLEMENT_REVERSAL_PENDING",
              "SettlementReversalPending",
              "PAYMENT_RESERVATION_RELEASED",
              "PaymentReservationReleased",
              "PAYMENT_REVERSED",
              "PaymentReversed",
              "DATE_CHANGE_DECLINED_BY_SUPPLIER",
              "DateChangeDeclinedBySupplier",
              "DATE_CHANGE_FAILED_BY_SUPPLIER",
              "DateChangeFailedBySupplier",
              "NameChangeRequestSubmitted",
              "NAME_CHANGE_REQUEST_SUBMITTED",
              "ManualNameChangeProcessing",
              "MANUAL_NAME_CHANGE_PROCESSING",
              "NameChangeQuoted",
              "NAME_CHANGE_QUOTED",
              "NameChangePaymentRequired",
              "NAME_CHANGE_PAYMENT_REQUIRED",
              "NameChangePaymentAuthorised",
              "NAME_CHANGE_PAYMENT_AUTHORISED",
              "NameChangePaymentAuthorized",
              "NAME_CHANGE_PAYMENT_AUTHORIZED",
              "NameChangeSubmittedToSupplier",
              "NAME_CHANGE_SUBMITTED_TO_SUPPLIER",
              "NameChangeFailed",
              "NAME_CHANGE_FAILED",
              "NameChangeQuoteExpired",
              "NAME_CHANGE_QUOTE_EXPIRED",
              "Paid",
              "PendingChange",
              "PendingReissue",
              "Pending Reissue",
              "Failed",
              "OpenTicketRequestSubmitted",
              "OPEN_TICKET_REQUEST_SUBMITTED",
              "OPEN_TICKET_VALIDATION_FAILED",
              "OpenTicketValidationFailed",
              "MANUAL_OPEN_TICKET_PROCESSING",
              "ManualOpenTicketProcessing",
              "OPEN_TICKET_PROCESSING_WITH_SUPPLIER",
              "OpenTicketProcessingWithSupplier",
              "OPEN_TICKET_PROCESSING_FAILED",
              "OpenTicketProcessingFailed",
              "AdditionalInformationRequired",
              "ADDITIONAL_INFORMATION_REQUIRED",
              "OpenTicketAdditionalInformationRequired",
              "OPEN_TICKET_ADDITIONAL_INFORMATION_REQUIRED",
            ]),
          );
        },
      },
      {
        name: "Rejected",
        onClick: () => {
          dispatch(setTicketsPage(1));
          dispatch(setTicketStatusType("Rejected"));
          dispatch(
            setTicketStatusFilterValues([
              "Denied",
              "Rejected",
              "Cancelled",
              "Canceled",
              "DATE_CHANGE_DECLINED_BY_AGENT",
              "DateChangeDeclinedByAgent",
              "DATE_CHANGE_DECLINED_BY_SUPPLIER",
              "DateChangeDeclinedBySupplier",
              "DATE_CHANGE_FAILED_BY_SUPPLIER",
              "DateChangeFailedBySupplier",
              "NameChangeRequestDenied",
              "NAME_CHANGE_REQUEST_DENIED",
              "NameChangeRequestCanceled",
              "NAME_CHANGE_REQUEST_CANCELED",
              "NameChangeRequestCancelled",
              "NAME_CHANGE_REQUEST_CANCELLED",
              "OPEN_TICKET_NOT_ELIGIBLE",
              "OpenTicketNotEligible",
              "OPEN_TICKET_DECLINED_BY_SUPPLIER",
              "OpenTicketDeclinedBySupplier",
              "OPEN_TICKET_REQUEST_CANCELED",
              "OpenTicketRequestCanceled",
              "OPEN_TICKET_REQUEST_CANCELLED",
              "OpenTicketRequestCancelled",
              "DATE_CHANGE_FAILURE_SETTLED",
              "DateChangeFailureSettled",
            ]),
          );
        },
      },
      {
        name: "Completed",
        onClick: () => {
          dispatch(setTicketsPage(1));
          dispatch(setTicketStatusType("Completed"));
          dispatch(
            setTicketStatusFilterValues([
              "Success",
              "Closed",
              "ChangedOrder",
              "DATE_CHANGE_COMPLETED",
              "DateChangeCompleted",
              "NameChangeCompleted",
              "NAME_CHANGE_COMPLETED",
              "OPEN_TICKET_SUCCESSFUL",
              "OpenTicketSuccessful",
              "OPEN_TICKET_COMPLETED",
              "OpenTicketCompleted",
            ]),
          );
        },
      },
    ],
  };

  const headerColumns = {
    Flights: [
      {
        name: "Order Number",
        type: "desc",
        width: "w-[25%]",
      },
      {
        name: "Trip",
        type: "asc",
        width: "w-[15%]",
      },
      {
        name: "Travel Time",
        type: "asc",
        width: "w-[15%]",
      },
      {
        name: "Passenger",
        type: "asc",
        width: "w-[15%]",
      },
      {
        name: "Point of sale",
        type: "asc",
        width: "w-[15%]",
      },
      {
        name: "Amount",
        type: "asc",
        width: "w-[15%]",
      },
      {
        name: "Status",
        type: "asc",
        width: "w-[25%]",
      },
    ],
    Tickets: [
      {
        name: "Order Number",
        type: "desc",
        width: "w-[25%]",
      },
      {
        name: "Trip",
        type: "asc",
        width: "w-[15%]",
      },
      {
        name: "Travel Time",
        type: "asc",
        width: "w-[15%]",
      },
      {
        name: "PNR",
        type: "asc",
        width: "w-[15%]",
      },
      {
        name: "Passenger",
        type: "asc",
        width: "w-[15%]",
      },
      {
        name: "Amount",
        type: "asc",
        width: "w-[15%]",
      },
      {
        name: "Status",
        type: "asc",
        width: "w-[25%]",
      },
    ],
    Voids: [
      {
        name: "Order Number",
        type: "desc",
        width: "w-[25%]",
      },
      {
        name: "Request Time",
        type: "desc",
        width: "w-[20%]",
      },
      {
        name: "Trip",
        type: "asc",
        width: "w-[15%]",
      },
      {
        name: "Travel Time",
        type: "asc",
        width: "w-[15%]",
      },
      {
        name: "PNR",
        type: "asc",
        width: "w-[15%]",
      },
      {
        name: "Passenger",
        type: "asc",
        width: "w-[15%]",
      },
      {
        name: "Amount",
        type: "asc",
        width: "w-[15%]",
      },
      {
        name: "Status",
        type: "asc",
        width: "w-[25%]",
      },
    ],
    Refunds: [
      {
        name: "Order Number",
        type: "desc",
        width: "w-[25%]",
      },
      {
        name: "Refund Request Time",
        type: "desc",
        width: "w-[20%]",
      },
      {
        name: "Trip",
        type: "asc",
        width: "w-[15%]",
      },
      {
        name: "Travel Time",
        type: "asc",
        width: "w-[15%]",
      },
      {
        name: "PNR",
        type: "asc",
        width: "w-[15%]",
      },
      {
        name: "Passenger",
        type: "asc",
        width: "w-[15%]",
      },
      {
        name: "Refund Amount",
        type: "asc",
        width: "w-[15%]",
      },
      {
        name: "Status",
        type: "asc",
        width: "w-[25%]",
      },
    ],
    Changes: [
      {
        name: "Order Number",
        type: "desc",
        width: "w-[22%]",
      },
      {
        name: "Requested change",
        type: "desc",
        width: "w-[18%]",
      },
      {
        name: "Trip",
        type: "asc",
        width: "w-[12%]",
      },
      {
        name: "Ticket",
        type: "asc",
        width: "w-[12%]",
      },
      {
        name: "PNR",
        type: "asc",
        width: "w-[12%]",
      },
      {
        name: "Passenger",
        type: "asc",
        width: "w-[12%]",
      },
      {
        name: "Fee",
        type: "asc",
        width: "w-[10%]",
      },
      {
        name: "Status",
        type: "asc",
        width: "w-[14%]",
      },
    ],
    DetailsChange: [
      {
        name: "Order Number",
        type: "desc",
        width: "w-[22%]",
      },
      {
        name: "Correction",
        type: "desc",
        width: "w-[18%]",
      },
      {
        name: "Trip",
        type: "asc",
        width: "w-[12%]",
      },
      {
        name: "Ticket",
        type: "asc",
        width: "w-[12%]",
      },
      {
        name: "PNR",
        type: "asc",
        width: "w-[12%]",
      },
      {
        name: "Passenger",
        type: "asc",
        width: "w-[12%]",
      },
      {
        name: "Fee",
        type: "asc",
        width: "w-[10%]",
      },
      {
        name: "Status",
        type: "asc",
        width: "w-[14%]",
      },
    ],
  };

  const ticketFilterTypes = {
    flight: [
      {
        name: "Bookings",
        onClick: () => {
          dispatch(setTicketFilterType("Bookings"));
          dispatch(setTicketStatusType("All"));
          dispatch(setFlightStatusFilterValues([]));
          dispatch(setTicketStatusFilterValues([]));
          selectSortHeader(0, headerColumns.Flights);
        },
      },
      {
        name: "Tickets",
        onClick: () => {
          dispatch(setTicketFilterType("Tickets"));
          dispatch(setTicketStatusType("All"));
          dispatch(setFlightStatusFilterValues([]));
          dispatch(setTicketStatusFilterValues([]));
          selectSortHeader(0, headerColumns.Tickets);
        },
      },
      {
        name: "Date Change",
        onClick: () => {
          dispatch(setTicketFilterType("Date Change"));
          dispatch(setTicketStatusType("All"));
          dispatch(setFlightStatusFilterValues([]));
          dispatch(setTicketStatusFilterValues([]));
          selectSortHeader(0, headerColumns.Changes);
        },
      },
      {
        name: "Details Change",
        onClick: () => {
          dispatch(setTicketFilterType("Details Change"));
          dispatch(setTicketStatusType("All"));
          dispatch(setFlightStatusFilterValues([]));
          dispatch(setTicketStatusFilterValues([]));
          selectSortHeader(0, headerColumns.DetailsChange);
        },
      },
      {
        name: "Open Ticket",
        onClick: () => {
          dispatch(setTicketFilterType("Open Ticket"));
          dispatch(setTicketStatusType("All"));
          dispatch(setFlightStatusFilterValues([]));
          dispatch(setTicketStatusFilterValues([]));
          selectSortHeader(0, headerColumns.Changes);
        },
      },
      {
        name: "Refund Orders",
        onClick: () => {
          dispatch(setTicketFilterType("Refund Orders"));
          dispatch(setTicketStatusType("All"));
          dispatch(setFlightStatusFilterValues([]));
          dispatch(setTicketStatusFilterValues([]));
          selectSortHeader(0, headerColumns.Refunds);
        },
      },
      {
        name: "Void Orders",
        onClick: () => {
          dispatch(setTicketFilterType("Void Orders"));
          dispatch(setTicketStatusType("All"));
          dispatch(setFlightStatusFilterValues([]));
          dispatch(setTicketStatusFilterValues([]));
          selectSortHeader(0, headerColumns.Voids);
        },
      },
      {
        name: "Freeze",
        onClick: () => {
          dispatch(setTicketFilterType("Freeze"));
          dispatch(setTicketStatusType("All"));
          dispatch(
            setFlightStatusFilterValues([
              "Initiated",
              "Approved",
              "Declined",
              "Cancelled",
              "Completed",
            ]),
          );
          dispatch(setTicketStatusFilterValues([]));
          selectSortHeader(0, headerColumns.Flights);
        },
      },
      // {
      //   name: "Completed",
      //   onClick: () => dispatch(setTicketFilterType("Completed")),
      // },
    ],
  };

  const hotelFilterType = [
    {
      name: "All",
      onClick: () => {
        dispatch(setTicketsPage(1));
        dispatch(setTicketFilterType("All"));
      },
    },
    {
      name: "Paid",
      onClick: () => {
        dispatch(setTicketsPage(1));
        dispatch(setTicketFilterType("Paid"));
      },
    },
    {
      name: "Pending",
      onClick: () => {
        dispatch(setTicketsPage(1));
        dispatch(setTicketFilterType("Pending"));
      },
    },
    {
      name: "Canceled",
      onClick: () => {
        dispatch(setTicketsPage(1));
        dispatch(setTicketFilterType("Canceled"));
      },
    },
    {
      name: "Refunded",
      onClick: () => {
        dispatch(setTicketsPage(1));
        dispatch(setTicketFilterType("Refunded"));
      },
    },
    {
      name: "Completed",
      onClick: () => {
        dispatch(setTicketsPage(1));
        dispatch(setTicketFilterType("Completed"));
      },
    },
  ];
  const tourFilterType = [
    {
      name: "All",
      onClick: () => {
        dispatch(setTicketsPage(1));
        dispatch(setTicketFilterType("All"));
      },
    },
    {
      name: "Paid",
      onClick: () => {
        dispatch(setTicketsPage(1));
        dispatch(setTicketFilterType("Paid"));
      },
    },
    {
      name: "Pending",
      onClick: () => {
        dispatch(setTicketsPage(1));
        dispatch(setTicketFilterType("Pending"));
      },
    },
    {
      name: "Canceled",
      onClick: () => {
        dispatch(setTicketsPage(1));
        dispatch(setTicketFilterType("Canceled"));
      },
    },
    {
      name: "Refunded",
      onClick: () => {
        dispatch(setTicketsPage(1));
        dispatch(setTicketFilterType("Refunded"));
      },
    },
    {
      name: "Completed",
      onClick: () => {
        dispatch(setTicketsPage(1));
        dispatch(setTicketFilterType("Completed"));
      },
    },
  ];

  const insuranceFilterType = [
    {
      name: "All",
      onClick: () => {
        dispatch(setTicketsPage(1));
        dispatch(setTicketFilterType("All"));
      },
    },
    {
      name: "Paid",
      onClick: () => {
        dispatch(setTicketsPage(1));
        dispatch(setTicketFilterType("Paid"));
      },
    },
    {
      name: "Pending",
      onClick: () => {
        dispatch(setTicketsPage(1));
        dispatch(setTicketFilterType("Pending"));
      },
    },
    {
      name: "Canceled",
      onClick: () => {
        dispatch(setTicketsPage(1));
        dispatch(setTicketFilterType("Canceled"));
      },
    },
    {
      name: "Refunded",
      onClick: () => {
        dispatch(setTicketsPage(1));
        dispatch(setTicketFilterType("Refunded"));
      },
    },
    {
      name: "Completed",
      onClick: () => {
        dispatch(setTicketsPage(1));
        dispatch(setTicketFilterType("Completed"));
      },
    },
  ];

  const packageFilterType = [
    {
      name: "All",
      onClick: () => {
        dispatch(setTicketsPage(1));
        dispatch(setTicketFilterType("All"));
      },
    },
    {
      name: "Paid",
      onClick: () => {
        dispatch(setTicketsPage(1));
        dispatch(setTicketFilterType("Paid"));
      },
    },
    {
      name: "Pending",
      onClick: () => {
        dispatch(setTicketsPage(1));
        dispatch(setTicketFilterType("Pending"));
      },
    },
    {
      name: "Canceled",
      onClick: () => {
        dispatch(setTicketsPage(1));
        dispatch(setTicketFilterType("Canceled"));
      },
    },
    {
      name: "Refunded",
      onClick: () => {
        dispatch(setTicketsPage(1));
        dispatch(setTicketFilterType("Refunded"));
      },
    },
    {
      name: "Completed",
      onClick: () => {
        dispatch(setTicketsPage(1));
        dispatch(setTicketFilterType("Completed"));
      },
    },
  ];

  const esimFilterType = [
    {
      name: "All",
      onClick: () => {
        dispatch(setTicketsPage(1));
        dispatch(setTicketFilterType("All"));
      },
    },
    {
      name: "Booked",
      onClick: () => {
        dispatch(setTicketsPage(1));
        dispatch(setTicketFilterType("Booked"));
      },
    },
    {
      name: "Paid",
      onClick: () => {
        dispatch(setTicketsPage(1));
        dispatch(setTicketFilterType("Paid"));
      },
    },
    {
      name: "Canceled",
      onClick: () => {
        dispatch(setTicketsPage(1));
        dispatch(setTicketFilterType("Canceled"));
      },
    },
  ];

  const exampleOrder = [
    {
      elements: [
        { type: "title", value: "20-07-2024 17:00:47 GMT+1" },
        { type: "copy", value: "40852853048358549475" },
        {
          type: "tag",
          value: "SABRE",
          color: "text-[#BD002A]",
          background: "bg-[#BD002A]/20",
        },
      ],
      width: "md:w-[25%]",
    },
    {
      elements: [
        { type: "title", value: "One Way" },
        { type: "text", value: "LOS - NBO KQ533" },
      ],
      width: "md:w-[15%]",
    },
    {
      elements: [
        { type: "title", value: "Travel Time" },
        { type: "text", value: "2024-07-30 12:25 - 19:45" },
      ],
      width: "md:w-[15%]",
    },
    {
      elements: [
        { type: "title", value: "Passenger" },
        { type: "text", value: "Chinedu John Doe" },
      ],
      width: "md:w-[15%]",
    },
    {
      elements: [
        { type: "title", value: "Price" },
        { type: "text", value: "1310.9 USD" },
      ],
      width: "md:w-[15%]",
    },
    {
      elements: [
        { type: "title", value: "Status" },
        { type: "status", value: "Awaiting payment" },
        { type: "time", value: "30 Mins" },
      ],
      width: "md:w-[15%]",
    },
  ];

  const getTicketFilterTypes = () => {
    switch (ticketType) {
      case configurations.stayKeyword:
        return hotelFilterType;
      case configurations.tourKeyword:
        return tourFilterType;
      case configurations.insuranceKeyword:
        return insuranceFilterType;
      case configurations.services.package.value:
        return packageFilterType;
      case configurations.services.esim.value:
        return esimFilterType;
      default:
        return ticketFilterTypes?.flight;
    }
  };

  const getTicketStatusFilterTypes = () => {
    if (ticketType === "Flights") {
      switch (ticketFilterType) {
        case "Tickets":
          return ticketFilters?.ticket;
        case "Void Orders":
          return ticketFilters?.voidTicket;
        case "Refund Orders":
          return ticketFilters?.refundTicket;
        case "Date Change":
          return ticketFilters?.dateChangeTicket;
        case "Details Change":
        case "Open Ticket":
          return ticketFilters?.changeTicket;
        case "Freeze":
          return ticketFilters?.freeze;
        default:
          return ticketFilters?.flight;
      }
    }
    return [];
  };

  const getSortHeaderRow = () => {
    // Prefer the header that matches the open queue so Details Change
    // never keeps showing Date Change's "Requested change" columns.
    if (ticketType === "Flights") {
      switch (ticketFilterType) {
        case "Details Change":
          return headerColumns.DetailsChange;
        case "Date Change":
        case "Open Ticket":
          return headerColumns.Changes;
        case "Void Orders":
          return headerColumns.Voids;
        case "Refund Orders":
          return headerColumns.Refunds;
        case "Tickets":
          return headerColumns.Tickets;
        case "Bookings":
        case "Freeze":
          return headerColumns.Flights;
        default:
          break;
      }
    }
    return ticketsSortHeader || headerColumns?.Flights;
  };

  const selectSortHeader = (index, selectedHeader) => {
    let items = [...(selectedHeader || getSortHeaderRow())];
    items = items.map((itm, i) => {
      return {
        name: itm?.name,
        type: itm?.type,
        width: itm?.width,
        index: i,
      };
    });
    let item = { ...items[index] };
    if (item?.name === selectedTicketsSortHeader?.name) {
      item.type = selectedHeader
        ? item?.type
        : item.type === "asc"
          ? "desc"
          : "asc";
    }
    items[index] = item;
    dispatch(setTicketsSortHeader(items));
    dispatch(setSelectedTicketsSortHeader(item));
  };

  function getFlightOrders() {
    const emptyFills = flightOrders?.filter((obj) => !obj) || [];
    // Show backend results as-is (filtering/search/pagination handled server-side)
    const filteredOrders = flightOrders?.filter((obj) => !!obj) || [];

    const templated = filteredOrders?.map((order) =>
      ticketFilterType === "Tickets"
        ? templateTicketsOrderData(order)
        : templateFlightOrderData(order),
    );

    const [sortedOrders, sortedIndices] = sortFlightOrders(
      templated,
      selectedTicketsSortHeader,
      filteredOrders,
    );

    return [
      [...(sortedOrders || []), ...emptyFills],
      [
        ...(sortArrayWithIndices(filteredOrders, sortedIndices) || []),
        ...emptyFills,
      ],
    ];
  }

  function getVoidOrders() {
    const emptyFills = voidOrders?.filter((obj) => !obj) || [];

    const filteredOrders = voidOrders?.filter((obj) => {
      if (!obj) return false;

      const bookingData = obj?.flightBooking?.at(0);
      let filter = true;

      // search
      try {
        if (ticketSearchValue && filter)
          filter = [
            bookingData?.bookingId,
            bookingData?.payable,
            bookingData?.customerPayable,
            bookingData?.orderId,
            bookingData?.pnr,
            bookingData?.supplier,
            bookingData?.travelers
              ?.map(
                (obj) =>
                  obj?.firstName + " " + obj?.middleName + " " + obj?.lastName,
              )
              ?.join(" "),
            bookingData?.flights
              ?.map(
                (obj) =>
                  obj?.airlineName +
                  " " +
                  obj?.arrivalAirport +
                  " " +
                  obj?.arrivalLocationName,
              )
              ?.join(" "),
            bookingData?.flights
              ?.map(
                (obj) =>
                  obj?.departureAirport + " " + obj?.departureLocationName,
              )
              ?.join(" "),
          ]
            ?.join(" ")
            ?.toLowerCase()
            ?.includes(ticketSearchValue?.toLowerCase());
      } catch (ex) {}

      return filter;
    });

    const templated = filteredOrders?.map((order) =>
      templateFlightOrderVoidData(order),
    );

    const [sortedOrders, sortedIndices] = sortFlightOrders(
      templated,
      selectedTicketsSortHeader,
      filteredOrders,
    );
    console.log(sortedOrders, "---");

    return [
      [...(sortedOrders || []), ...emptyFills],
      [
        ...(sortArrayWithIndices(filteredOrders, sortedIndices) || []),
        ...emptyFills,
      ],
    ];

    // return [sortedOrders,sortArrayWithIndices(filteredOrders,sortedIndices)||[]];
  }

  function getRefundOrders() {
    const emptyFills = refundOrders?.filter((obj) => !obj) || [];

    const filteredOrders = refundOrders?.filter((obj) => {
      if (!obj) return false;

      const bookingData = obj?.flightBooking?.at(0);
      let filter = true;

      // search
      try {
        if (ticketSearchValue && filter)
          filter = [
            bookingData?.bookingId,
            bookingData?.payable,
            bookingData?.customerPayable,
            bookingData?.orderId,
            bookingData?.pnr,
            bookingData?.supplier,
            bookingData?.travelers
              ?.map(
                (obj) =>
                  obj?.firstName + " " + obj?.middleName + " " + obj?.lastName,
              )
              ?.join(" "),
            bookingData?.flights
              ?.map(
                (obj) =>
                  obj?.airlineName +
                  " " +
                  obj?.arrivalAirport +
                  " " +
                  obj?.arrivalLocationName,
              )
              ?.join(" "),
            bookingData?.flights
              ?.map(
                (obj) =>
                  obj?.departureAirport + " " + obj?.departureLocationName,
              )
              ?.join(" "),
          ]
            ?.join(" ")
            ?.toLowerCase()
            ?.includes(ticketSearchValue?.toLowerCase());
      } catch (ex) {}

      return filter;
    });

    const templated = filteredOrders?.map((order) =>
      templateFlightOrderRefundData(order),
    );

    const [sortedOrders, sortedIndices] = sortFlightOrders(
      templated,
      selectedTicketsSortHeader,
      filteredOrders,
    );

    return [
      [...(sortedOrders || []), ...emptyFills],
      [
        ...(sortArrayWithIndices(filteredOrders, sortedIndices) || []),
        ...emptyFills,
      ],
    ];
    // return [sortedOrders,sortArrayWithIndices(filteredOrders,sortedIndices)||[]];
  }

  function getReissueOrders() {
    const emptyFills = reissueOrders?.filter((obj) => !obj) || [];

    // console.log({ reissueOrders });

    const filteredOrders = reissueOrders?.filter((obj) => {
      if (!obj) return false;

      const bookingData = Array.isArray(obj?.flightBooking)
        ? obj.flightBooking.at(0)
        : obj?.flightBooking;
      let filter = true;

      // search
      try {
        if (ticketSearchValue && filter)
          filter = [
            obj?.orderId,
            obj?.bookingId,
            obj?.status,
            bookingData?.bookingId,
            bookingData?.payable,
            bookingData?.customerPayable,
            bookingData?.orderId,
            bookingData?.pnr,
            bookingData?.supplier,
            bookingData?.travelers
              ?.map(
                (traveler) =>
                  traveler?.firstName +
                  " " +
                  traveler?.middleName +
                  " " +
                  traveler?.lastName,
              )
              ?.join(" "),
            bookingData?.travelers
              ?.flatMap((traveler) => traveler?.ticketNumbers || [])
              ?.join(" "),
            obj?.newDirections
              ?.map(
                (direction) =>
                  `${direction?.from || ""} ${direction?.to || ""} ${
                    typeof direction?.departure === "string"
                      ? direction.departure
                      : direction?.departure?.date ||
                        direction?.date ||
                        direction?.departureDate ||
                        ""
                  }`,
              )
              ?.join(" "),
            bookingData?.flights
              ?.map(
                (flight) =>
                  flight?.airlineName +
                  " " +
                  flight?.arrivalAirport +
                  " " +
                  flight?.arrivalLocationName,
              )
              ?.join(" "),
            bookingData?.flights
              ?.map(
                (flight) =>
                  flight?.departureAirport +
                  " " +
                  flight?.departureLocationName,
              )
              ?.join(" "),
          ]
            ?.join(" ")
            ?.toLowerCase()
            ?.includes(ticketSearchValue?.toLowerCase());
      } catch (ex) {}

      return filter;
    });

    const templated = filteredOrders?.map((order) =>
      templateFlightOrderReissueData(order),
    );

    const [sortedOrders, sortedIndices] = sortFlightOrders(
      templated,
      selectedTicketsSortHeader,
      filteredOrders,
    );
    console.log(sortedOrders, "---");

    return [
      [...(sortedOrders || []), ...emptyFills],
      [
        ...(sortArrayWithIndices(filteredOrders, sortedIndices) || []),
        ...emptyFills,
      ],
    ];
    // return [sortedOrders,sortArrayWithIndices(filteredOrders,sortedIndices)||[]];
  }

  function getRenameOrders() {
    const emptyFills = renameOrders?.filter((obj) => !obj) || [];

    const filteredOrders = renameOrders?.filter((obj) => {
      if (!obj) return false;

      const bookingData = obj?.flightBooking?.at(0);
      let filter = true;

      // search
      try {
        if (ticketSearchValue && filter)
          filter = [
            bookingData?.bookingId,
            bookingData?.payable,
            bookingData?.customerPayable,
            bookingData?.orderId,
            bookingData?.pnr,
            bookingData?.supplier,
            bookingData?.travelers
              ?.map(
                (obj) =>
                  obj?.firstName + " " + obj?.middleName + " " + obj?.lastName,
              )
              ?.join(" "),
            bookingData?.flights
              ?.map(
                (obj) =>
                  obj?.airlineName +
                  " " +
                  obj?.arrivalAirport +
                  " " +
                  obj?.arrivalLocationName,
              )
              ?.join(" "),
            bookingData?.flights
              ?.map(
                (obj) =>
                  obj?.departureAirport + " " + obj?.departureLocationName,
              )
              ?.join(" "),
          ]
            ?.join(" ")
            ?.toLowerCase()
            ?.includes(ticketSearchValue?.toLowerCase());
      } catch (ex) {}

      return filter;
    });

    const templated = filteredOrders?.map((order) =>
      templateFlightOrderRenameData(order),
    );

    const [sortedOrders, sortedIndices] = sortFlightOrders(
      templated,
      selectedTicketsSortHeader,
      filteredOrders,
    );
    // console.log(sortedOrders, "---");

    return [
      [...(sortedOrders || []), ...emptyFills],
      [
        ...(sortArrayWithIndices(filteredOrders, sortedIndices) || []),
        ...emptyFills,
      ],
    ];
    // return [sortedOrders,sortArrayWithIndices(filteredOrders,sortedIndices)||[]];
  }

  function getOpenTicketOrders() {
    const emptyFills = openTicketOrders?.filter((obj) => !obj) || [];

    const filteredOrders = openTicketOrders?.filter((obj) => {
      if (!obj) return false;

      const bookingData = obj?.flightBooking?.at(0);
      let filter = true;

      try {
        if (ticketSearchValue && filter)
          filter = [
            obj?.orderId,
            obj?.bookingId,
            bookingData?.bookingId,
            bookingData?.orderId,
            bookingData?.pnr,
            bookingData?.supplier,
            bookingData?.travelers
              ?.map(
                (traveler) =>
                  traveler?.firstName +
                  " " +
                  traveler?.middleName +
                  " " +
                  traveler?.lastName,
              )
              ?.join(" "),
            bookingData?.flights
              ?.map(
                (flight) =>
                  flight?.airlineName +
                  " " +
                  flight?.arrivalAirport +
                  " " +
                  flight?.arrivalLocationName,
              )
              ?.join(" "),
            (obj?.originalItinerary || [])
              ?.map(
                (leg) =>
                  `${leg?.airlineName || ""} ${leg?.departureLocation || ""} ${leg?.arrivalLocation || ""}`,
              )
              ?.join(" "),
          ]
            ?.join(" ")
            ?.toLowerCase()
            ?.includes(ticketSearchValue?.toLowerCase());
      } catch (ex) {}

      return filter;
    });

    const templated = filteredOrders?.map((order) =>
      templateFlightOrderOpenTicketData(order),
    );

    const [sortedOrders, sortedIndices] = sortFlightOrders(
      templated,
      selectedTicketsSortHeader,
      filteredOrders,
    );

    return [
      [...(sortedOrders || []), ...emptyFills],
      [
        ...(sortArrayWithIndices(filteredOrders, sortedIndices) || []),
        ...emptyFills,
      ],
    ];
  }

  function getStaysOrders() {
    const filteredOrders = hotelOrders?.filter((obj) => {
      // const bookingData = obj?.flightBooking?.at(0);
      let filter = true;

      // search
      // try {
      //   if (ticketSearchValue && filter)
      //     filter = [
      //       obj?.bookingId,
      //       obj?.fareBasePrice,
      //       obj?.bookRef,
      //       obj?.holdBookingRes?.name,
      //       obj?.supplier,
      //       obj?.name,
      //     ]
      //       ?.join(" ")
      //       ?.toLowerCase()
      //       ?.includes(ticketSearchValue?.toLowerCase());
      // } catch (ex) {}

      // if (ticketFilterType && ticketFilterType !== "All") {
      //   switch (ticketFilterType) {
      //     case "Paid":
      //       return ["Paid"]?.includes(obj?.status);
      //     case "Pending":
      //       return ["Pending", "Booked"]?.includes(obj?.status);
      //     case "Canceled":
      //       return ["Canceled", "Cancelled"]?.includes(obj?.status);
      //     case "Refunded":
      //       return ["Refunded"]?.includes(obj?.status);
      //     case "Completed":
      //       return ["Issued", "Ticket issued", "Completed"]?.includes(
      //         obj?.status
      //       );
      //     default:
      //       return true;
      //   }
      // }

      return filter;
    });

    const templated = filteredOrders?.map((order) =>
      templateStayOrderData(order),
    );

    const [sortedOrders, sortedIndices] = sortFlightOrders(
      templated,
      selectedTicketsSortHeader,
      filteredOrders,
    );

    return [
      sortedOrders,
      sortArrayWithIndices(filteredOrders, sortedIndices) || [],
    ];
  }

  function getTourOrders() {
    const filteredOrders = tourOrders?.filter((obj) => {
      // const bookingData = obj?.flightBooking?.at(0);
      let filter = true;

      // search
      // try {
      //   if (ticketSearchValue && filter)
      //     filter = [
      //       obj?.bookingId,
      //       obj?.amount,
      //       obj?.bookRef,
      //       obj?.provider,
      //       obj?.name,
      //       obj?.extraDetails?.productInfo?.title,
      //     ]
      //       ?.join(" ")
      //       ?.toLowerCase()
      //       ?.includes(ticketSearchValue?.toLowerCase());
      // } catch (ex) {}

      // if (ticketFilterType && ticketFilterType !== "All") {
      //   switch (ticketFilterType) {
      //     case "Paid":
      //       return ["Paid"]?.includes(obj?.status);
      //     case "Pending":
      //       return ["Pending", "Booked"]?.includes(obj?.status);
      //     case "Canceled":
      //       return ["Canceled", "Cancelled"]?.includes(obj?.status);
      //     case "Refunded":
      //       return ["Refunded"]?.includes(obj?.status);
      //     case "Completed":
      //       return ["Issued", "Ticket issued"]?.includes(obj?.status);
      //     default:
      //       return true;
      //   }
      // }

      return filter;
    });

    const templated = filteredOrders?.map((order) =>
      templateTourOrderData(order),
    );

    const [sortedOrders, sortedIndices] = sortFlightOrders(
      templated,
      selectedTicketsSortHeader,
      filteredOrders,
    );

    return [
      sortedOrders,
      sortArrayWithIndices(filteredOrders, sortedIndices) || [],
    ];
  }

  function getInsuranceOrders() {
    const filteredOrders = insuranceOrders?.filter((obj) => {
      // const bookingData = obj?.flightBooking?.at(0);
      let filter = true;

      // search
      // try {
      //   if (ticketSearchValue && filter)
      //     filter = [
      //       obj?.bookingId,
      //       obj?.amount,
      //       obj?.bookRef,
      //       obj?.provider,
      //       obj?.name,
      //       obj?.title,
      //     ]
      //       ?.join(" ")
      //       ?.toLowerCase()
      //       ?.includes(ticketSearchValue?.toLowerCase());
      // } catch (ex) {}

      // if (ticketFilterType && ticketFilterType !== "All") {
      //   switch (ticketFilterType) {
      //     case "Paid":
      //       return ["Paid"]?.includes(obj?.status);
      //     case "Pending":
      //       return ["Pending", "Booked"]?.includes(obj?.status);
      //     case "Canceled":
      //       return ["Canceled", "Cancelled"]?.includes(obj?.status);
      //     case "Refunded":
      //       return ["Refunded"]?.includes(obj?.status);
      //     case "Completed":
      //       return ["Issued", "Ticket issued", "Completed"]?.includes(
      //         obj?.status
      //       );
      //     default:
      //       return true;
      //   }
      // }

      return filter;
    });

    const templated = filteredOrders?.map((order) =>
      templateInsuranceOrderData(order),
    );

    const [sortedOrders, sortedIndices] = sortFlightOrders(
      templated,
      selectedTicketsSortHeader,
      filteredOrders,
    );

    return [
      sortedOrders,
      sortArrayWithIndices(filteredOrders, sortedIndices) || [],
    ];
  }

  function getPackageOrders() {
    const filteredOrders = packageOrders?.filter((obj) => {
      // const bookingData = obj?.flightBooking?.at(0);
      let filter = true;

      // search
      // try {
      //   if (ticketSearchValue && filter)
      //     filter = [
      //       obj?.bookingId,
      //       obj?.amount,
      //       obj?.bookRef,
      //       obj?.provider,
      //       obj?.name,
      //       obj?.title,
      //     ]
      //       ?.join(" ")
      //       ?.toLowerCase()
      //       ?.includes(ticketSearchValue?.toLowerCase());
      // } catch (ex) {}

      // if (ticketFilterType && ticketFilterType !== "All") {
      //   switch (ticketFilterType) {
      //     case "Paid":
      //       return ["Paid"]?.includes(obj?.status);
      //     case "Pending":
      //       return ["Pending", "Booked"]?.includes(obj?.status);
      //     case "Canceled":
      //       return ["Canceled", "Cancelled"]?.includes(obj?.status);
      //     case "Refunded":
      //       return ["Refunded"]?.includes(obj?.status);
      //     case "Completed":
      //       return ["Issued", "Ticket issued"]?.includes(obj?.status);
      //     default:
      //       return true;
      //   }
      // }

      return filter;
    });

    const templated = filteredOrders?.map((order) =>
      templatePackageOrderData(order),
    );

    const [sortedOrders, sortedIndices] = sortFlightOrders(
      templated,
      selectedTicketsSortHeader,
      filteredOrders,
    );

    return [
      sortedOrders,
      sortArrayWithIndices(filteredOrders, sortedIndices) || [],
    ];
  }

  function getEsimOrders() {
    const filteredOrders = esimOrders?.filter((obj) => {
      let filter = true;

      if (ticketFilterType && ticketFilterType !== "All") {
        switch (ticketFilterType) {
          case "Booked":
            filter = ["Booked", "Pending", "Not Paid"]?.includes(obj?.status);
            break;
          case "Paid":
            filter = ["Paid", "Issued", "Completed"]?.includes(obj?.status);
            break;
          case "Canceled":
            filter = ["Canceled", "Cancelled"]?.includes(obj?.status);
            break;
          default:
            filter = true;
        }
      }

      return filter;
    });

    const templated = filteredOrders?.map((order) =>
      templateEsimOrderData(order),
    );

    const [sortedOrders, sortedIndices] = sortFlightOrders(
      templated,
      selectedTicketsSortHeader,
      filteredOrders,
    );

    return [
      sortedOrders,
      sortArrayWithIndices(filteredOrders, sortedIndices) || [],
    ];
  }

  const getOrders = () => {
    switch (ticketType) {
      case "Flights":
        switch (ticketFilterType) {
          case "Void Orders":
            return getVoidOrders();
          case "Refund Orders":
            return getRefundOrders();
          case "Date Change":
            return getReissueOrders();
          case "Details Change":
            return getRenameOrders();
          case "Open Ticket":
            return getOpenTicketOrders();
          default:
            return getFlightOrders();
        }
      // case configurations.services.stay.value:
      case configurations.stayKeyword:
        return getStaysOrders();
      // case configurations.services.tour.value:
      case configurations.tourKeyword:
        return getTourOrders();
      // case configurations.services.insurance.value:
      case configurations.insuranceKeyword:
        return getInsuranceOrders();
      case configurations.services.package.value:
        return getPackageOrders();
      case configurations.services.esim.value:
        return getEsimOrders();
      default:
        return getFlightOrders();
    }
  };

  return {
    getOrdersCount,
    selectOrderType,
    getTicketFilterTypes,
    getTicketStatusFilterTypes,
    getSortHeaderRow,
    selectSortHeader,
    getOrders,
  };
}

// export enum ORDER_STATUS {
//   Approved = "Approved",
//   Paid = "Paid",
//   Denied = "Denied",
//   Pending = "Pending",
//   PendingPayment = "PendingPayment",
//   PendingVoid = "PendingVoid",
//   Success = "Success",
//   Failed = "Failed",
//   Closed = "Closed",
// }
